import { useMemo, useState } from 'react';

import { Box } from '@mui/material';

import useForwardCurveChartSeries from '@/components/Chart/hooks/useForwardCurveChartSeries';
import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import useInViewport from '@/hooks/useInViewport';
import BasicChartBlock from '@/pages/Markets/MarketsComponents/Chart/BasicChartBlock';
import ButtonSection from '@/pages/Markets/MarketsComponents/Chart/ForwardCurveChartBlock/ButtonSection';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

type ForwardCurveChartType = {
  layout?: { fullWidth?: boolean; height?: number };
  templateChartSeries?: TemplateChartSeries[];
};

const mapWithReturnedTransactionDate = (
  comparisonTemplateSeries: TemplateChartSeries[],
  chartSeries: ExtendedSeriesOptionsType[] | undefined
) => {
  return comparisonTemplateSeries.map((item, i) => {
    if (!chartSeries?.[i + 1]) return item;
    return {
      ...item,
      symbol: {
        ...item.symbol,
        transactionDate: chartSeries[i + 1].custom.transactionDate
      }
    };
  });
};

const ForwardCurveChartBlock = ({ layout, templateChartSeries }: ForwardCurveChartType) => {
  const [containerRef, isInViewport] = useInViewport();
  const [comparisonTemplateSeries, setComparisonTemplateSeries] = useState<TemplateChartSeries[]>(
    []
  );

  const { loading, error, chartSeries } = useForwardCurveChartSeries(
    templateChartSeries && [...templateChartSeries, ...comparisonTemplateSeries],
    setComparisonTemplateSeries,
    isInViewport
  );

  const mappedComparisonTemplate = useMemo(() => {
    return mapWithReturnedTransactionDate(comparisonTemplateSeries, chartSeries);
  }, [chartSeries, comparisonTemplateSeries]);
  const buttonSection = useMemo(() => {
    if (!chartSeries?.length) return null;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ButtonSection
          chartSeries={chartSeries}
          comparisonTemplateSeries={mappedComparisonTemplate}
          setComparisonTemplateSeries={setComparisonTemplateSeries}
          templateChartSeries={templateChartSeries}
        />
      </Box>
    );
  }, [templateChartSeries, mappedComparisonTemplate, setComparisonTemplateSeries, chartSeries]);

  return (
    <BasicChartBlock
      buttonSection={buttonSection}
      chartSeries={chartSeries}
      chartType='forwardSimple'
      containerRef={containerRef}
      error={error}
      layout={layout}
      loading={loading}
    />
  );
};

export default ForwardCurveChartBlock;
